import { useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { Button, Field, StickyBottomActions } from "../../design-v2";
import { startEmailAuthentication } from "../../services/api";
import AuthLayout from "../AuthLayout";
import { getAuthenticatedDestination } from "../authRouting";
import { readValidatedNext } from "../returnPath";
import useAuthBootstrap from "../useAuthBootstrap";
import { rememberLastUsedAuthProvider } from "../components/ProviderButton";

const EMAIL_ERRORS = Object.freeze({
  INVALID: "invalid_email",
  THROTTLED: "throttled",
  DELIVERY: "delivery_failed",
  REQUEST: "request_retry",
  NETWORK: "network_error",
});

function isValidEmail(email) {
  const value = String(email || "").trim();
  return value.length <= 254 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

function errorCopy(error) {
  switch (error) {
    case EMAIL_ERRORS.THROTTLED:
      return "Too many requests. Try again in a few minutes.";
    case EMAIL_ERRORS.DELIVERY:
      return "We couldn't send your code. Please try again.";
    case EMAIL_ERRORS.REQUEST:
      return "We couldn't complete that request. Please try again.";
    case EMAIL_ERRORS.NETWORK:
      return "We couldn't reach Lyft Zone. Check your connection and try again.";
    default:
      return "";
  }
}

export default function EmailEntryScreen() {
  const location = useLocation();
  const navigate = useNavigate();
  const bootstrap = useAuthBootstrap();
  const next = readValidatedNext(location.search);
  const [email, setEmail] = useState(() => String(location.state?.email || ""));
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  if (bootstrap.status === "authenticated") {
    return (
      <Navigate
        to={getAuthenticatedDestination(bootstrap.onboarding, next)}
        replace
      />
    );
  }

  const goBack = () => {
    if (window.history.state?.idx > 0) {
      navigate(-1);
    } else {
      navigate("/", { replace: true });
    }
  };

  const submit = async (event) => {
    event?.preventDefault?.();
    if (submitting) {
      return;
    }
    if (!isValidEmail(email)) {
      setError(EMAIL_ERRORS.INVALID);
      return;
    }

    const normalizedEmail = email.trim().toLowerCase();
    setSubmitting(true);
    setError(null);
    try {
      const result = await startEmailAuthentication(normalizedEmail);
      if (result?.devAutoVerified === true) {
        rememberLastUsedAuthProvider("email");
        navigate("/auth/return", { replace: true, state: { next } });
        return;
      }
      navigate("/auth/email/verify", {
        state: { email: normalizedEmail, next },
      });
    } catch (requestError) {
      if (requestError?.code === "INVALID_EMAIL") {
        setError(EMAIL_ERRORS.INVALID);
      } else if (requestError?.code === "OTP_RATE_LIMITED") {
        setError(EMAIL_ERRORS.THROTTLED);
      } else if (requestError?.code === "EMAIL_DELIVERY_FAILED") {
        setError(EMAIL_ERRORS.DELIVERY);
      } else if (requestError?.code === "OTP_CONCURRENCY_RETRY") {
        setError(EMAIL_ERRORS.REQUEST);
      } else {
        setError(EMAIL_ERRORS.NETWORK);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const message = errorCopy(error);

  return (
    <AuthLayout onBack={goBack} backLabel="Go back" hasStickyActions>
      <form className="lz-auth-form" onSubmit={submit} noValidate>
        <div className="lz-auth-form__body">
          <div className="lz-auth-intro">
            <h1>CONTINUE WITH EMAIL.</h1>
            <p>We&apos;ll send you a one-time verification code. No password needed.</p>
          </div>

          <Field
            label="Email address"
            required
            optionalLabel=""
            error={error === EMAIL_ERRORS.INVALID ? "Enter a valid email address." : ""}
          >
            <input
              type="email"
              inputMode="email"
              autoComplete="email"
              placeholder="you@email.com"
              value={email}
              disabled={submitting}
              onChange={(event) => {
                setEmail(event.target.value);
                if (error) {
                  setError(null);
                }
              }}
            />
          </Field>

          {message ? (
            <div className="lz-auth-alert" role="alert">
              <span>{message}</span>
              {error === EMAIL_ERRORS.NETWORK ||
              error === EMAIL_ERRORS.DELIVERY ||
              error === EMAIL_ERRORS.REQUEST ? (
                <Button variant="ghost" onClick={submit} disabled={submitting}>
                  Try again
                </Button>
              ) : null}
            </div>
          ) : null}
        </div>

        <StickyBottomActions>
          <Button
            type="submit"
            size="lg"
            className="lz-auth-primary-action"
            disabled={!email.trim()}
            isLoading={submitting}
            loadingLabel="Sending..."
          >
            Continue
          </Button>
        </StickyBottomActions>
      </form>
    </AuthLayout>
  );
}

export { EMAIL_ERRORS, isValidEmail };
